import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  AlertTriangle, 
  CheckCircle,
  Clock,
  Send,
  FileText,
  User,
  Building2,
  Wallet,
  Globe,
  Shield,
  Zap,
  MessageSquare
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import ReactFlow, { Background, Controls } from "reactflow";
import "reactflow/dist/style.css";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseDetail() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchCase = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}`);
      setCaseData(response.data);
    } catch (error) {
      toast.error("Failed to load case details");
      navigate("/cases");
    } finally {
      setLoading(false);
    }
  }, [caseId, navigate]);

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  const updateStatus = async (newStatus) => {
    try {
      await axios.patch(`${API}/cases/${caseId}`, { status: newStatus });
      toast.success("Status updated");
      fetchCase();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSubmitting(true);
    try {
      await axios.patch(`${API}/cases/${caseId}`, { newNote: newNote.trim(), author: 'Analyst' });
      toast.success("Note added");
      setNewNote("");
      fetchCase();
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="inline-flex items-center gap-3">
          <div className="h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-400">Loading case...</span>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return <div className="p-8 text-center text-slate-500">Case not found</div>;
  }

  const { client } = caseData;
  const bandConfig = {
    RED: { 
      gradient: "from-red-500/30 to-red-600/10",
      textGradient: "gradient-text-risk-red",
      dot: "risk-dot-red",
      label: "HIGH RISK"
    },
    AMBER: { 
      gradient: "from-amber-500/30 to-amber-600/10",
      textGradient: "gradient-text-risk-amber",
      dot: "risk-dot-amber",
      label: "MEDIUM RISK"
    },
    GREEN: { 
      gradient: "from-emerald-500/30 to-emerald-600/10",
      textGradient: "gradient-text-risk-green",
      dot: "risk-dot-green",
      label: "LOW RISK"
    }
  };
  const config = bandConfig[caseData.band] || bandConfig.GREEN;

  const nodes = (client?.network?.nodes || []).map((node, i) => ({
    id: node.id,
    position: { x: 180 + (i % 3) * 160, y: 80 + Math.floor(i / 3) * 120 },
    data: { label: node.label },
    style: {
      background: node.risk === 'high' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(30, 41, 59, 0.8)',
      border: node.risk === 'high' ? '2px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(99, 102, 241, 0.3)',
      borderRadius: '12px',
      padding: '10px 14px',
      color: '#f8fafc',
      fontSize: '11px',
      backdropFilter: 'blur(10px)'
    }
  }));

  const edges = (client?.network?.edges || []).map(edge => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    label: edge.label,
    labelStyle: { fill: '#94a3b8', fontSize: 9 },
    style: { stroke: 'rgba(99, 102, 241, 0.4)', strokeWidth: 2 }
  }));

  return (
    <div className="space-y-6" data-testid="case-detail-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/cases")}
            className="hover:bg-white/10 rounded-xl"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">{caseData.clientName}</h1>
            <p className="text-sm text-slate-500 font-mono-data">Case #{caseData.caseId.slice(0, 12)}</p>
          </div>
        </div>
        <Button 
          variant="outline"
          onClick={() => navigate(`/reports?clientId=${client?.clientId}`)}
          className="border-white/10 hover:bg-white/5 rounded-xl"
          data-testid="generate-report-btn"
        >
          <FileText className="h-4 w-4 mr-2" />
          Generate Report
        </Button>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Risk Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className={`glass-card rounded-2xl border-0 overflow-hidden`}>
              <div className={`absolute inset-0 bg-gradient-to-br ${config.gradient} opacity-30`} />
              <CardContent className="p-8 relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-2">Risk Score</p>
                    <div className="flex items-baseline gap-3">
                      <span className={`text-6xl font-bold ${config.textGradient}`} style={{ fontFamily: 'JetBrains Mono' }}>
                        {caseData.riskScore}
                      </span>
                      <span className="text-2xl text-slate-500">/100</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-white/5 border border-white/10">
                    <div className={`risk-dot ${config.dot}`} style={{ width: 12, height: 12 }} />
                    <span className={`text-lg font-bold ${config.textGradient}`}>{config.label}</span>
                  </div>
                </div>
                {client?.recommendation && (
                  <p className="mt-6 text-sm text-slate-400 leading-relaxed">{client.recommendation}</p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Risk Factors */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="glass-card rounded-2xl border-0 overflow-hidden">
              <div className="px-6 py-5 border-b border-white/5 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20">
                  <Shield className="h-4 w-4 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Screening Results</h3>
                  <p className="text-xs text-slate-500">Risk factor breakdown</p>
                </div>
              </div>
              <CardContent className="p-0 divide-y divide-white/5">
                {client?.factors?.map((factor, index) => (
                  <Collapsible key={index}>
                    <CollapsibleTrigger className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
                      <div className="flex items-center gap-4">
                        <div className={`risk-dot ${factor.hit ? 'risk-dot-red' : 'risk-dot-green'}`} />
                        <span className="text-sm font-medium text-white">{factor.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-bold font-mono-data ${factor.hit ? 'text-red-400' : 'text-slate-500'}`}>
                          {factor.hit ? `+${factor.points}` : '0'} pts
                        </span>
                        {factor.hit ? (
                          <AlertTriangle className="h-4 w-4 text-red-400" />
                        ) : (
                          <CheckCircle className="h-4 w-4 text-emerald-400" />
                        )}
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="px-6 pb-4">
                      <div className="pl-8 border-l-2 border-slate-700 ml-1 text-sm text-slate-400">
                        <p className="text-xs text-slate-500 mb-1">Source: {factor.source}</p>
                        <p>{factor.details}</p>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </CardContent>
            </Card>
          </motion.div>

          {/* Network Graph */}
          {nodes.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="glass-card rounded-2xl border-0 overflow-hidden">
                <div className="px-6 py-5 border-b border-white/5 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20">
                    <Zap className="h-4 w-4 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Entity Network</h3>
                    <p className="text-xs text-slate-500">Relationship mapping</p>
                  </div>
                </div>
                <CardContent className="p-0">
                  <div className="h-[280px]">
                    <ReactFlow nodes={nodes} edges={edges} fitView>
                      <Background color="rgba(99, 102, 241, 0.1)" gap={25} />
                      <Controls className="bg-slate-900/80 border-slate-700 rounded-lg" />
                    </ReactFlow>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Notes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="glass-card rounded-2xl border-0 overflow-hidden">
              <div className="px-6 py-5 border-b border-white/5 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20">
                  <MessageSquare className="h-4 w-4 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Investigation Notes</h3>
                  <p className="text-xs text-slate-500">Case documentation</p>
                </div>
              </div>
              <CardContent className="p-6 space-y-4">
                <div className="flex gap-3">
                  <Textarea 
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add investigation notes..."
                    className="input-premium rounded-xl resize-none"
                    rows={3}
                    data-testid="note-input"
                  />
                  <Button 
                    onClick={addNote}
                    disabled={submitting || !newNote.trim()}
                    className="btn-premium rounded-xl px-4"
                    data-testid="add-note-btn"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                <ScrollArea className="h-[200px]">
                  {caseData.notes?.length > 0 ? (
                    <div className="space-y-3">
                      {caseData.notes.map((note, i) => (
                        <motion.div 
                          key={i} 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="glass-card rounded-xl p-4"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-blue-400">{note.author}</span>
                            <span className="text-xs text-slate-500 font-mono-data">
                              {new Date(note.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-300">{note.content}</p>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-8">No notes yet</p>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Case Status */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="glass-card rounded-2xl border-0">
              <div className="px-5 py-4 border-b border-white/5">
                <h4 className="text-sm font-semibold text-white">Case Status</h4>
              </div>
              <CardContent className="p-5 space-y-4">
                <Select value={caseData.status} onValueChange={updateStatus}>
                  <SelectTrigger className="input-premium h-11 rounded-xl" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="glass-card border-white/10">
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="sar_filed">SAR Filed</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-slate-500 space-y-1">
                  <p>Created: {new Date(caseData.createdAt).toLocaleDateString()}</p>
                  <p>Updated: {new Date(caseData.updatedAt).toLocaleDateString()}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Client Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="glass-card rounded-2xl border-0">
              <div className="px-5 py-4 border-b border-white/5">
                <h4 className="text-sm font-semibold text-white">Client Information</h4>
              </div>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20">
                    <User className="h-4 w-4 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{client?.name}</p>
                    <p className="text-xs text-slate-500">{client?.email}</p>
                  </div>
                </div>
                <Separator className="bg-white/5" />
                {client?.address?.country && (
                  <div className="flex items-center gap-3">
                    <Globe className="h-4 w-4 text-slate-500" />
                    <span className="text-sm text-slate-300">{client.address.country}</span>
                  </div>
                )}
                {client?.business?.companyName && (
                  <div className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-slate-500" />
                    <span className="text-sm text-slate-300">{client.business.companyName}</span>
                  </div>
                )}
                {client?.wallets?.length > 0 && (
                  <div className="flex items-start gap-3">
                    <Wallet className="h-4 w-4 text-slate-500 mt-0.5" />
                    <div className="text-sm text-slate-300 font-mono-data text-xs">
                      {client.wallets.map((w, i) => (
                        <p key={i} className="truncate">{w.address?.slice(0, 18)}...</p>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="glass-card rounded-2xl border-0">
              <div className="px-5 py-4 border-b border-white/5">
                <h4 className="text-sm font-semibold text-white">Quick Actions</h4>
              </div>
              <CardContent className="p-5 space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-white/10 hover:bg-emerald-500/10 hover:border-emerald-500/30 rounded-xl"
                  onClick={() => updateStatus('resolved')}
                >
                  <CheckCircle className="h-4 w-4 mr-2 text-emerald-400" />
                  Approve Client
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-white/10 hover:bg-amber-500/10 hover:border-amber-500/30 rounded-xl"
                  onClick={() => updateStatus('in_progress')}
                >
                  <Clock className="h-4 w-4 mr-2 text-amber-400" />
                  Request EDD
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-red-500/20 text-red-400 hover:bg-red-500/10 rounded-xl"
                  onClick={() => updateStatus('sar_filed')}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  File SAR
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
